// src/components/auth/Login.js

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate, Link } from 'react-router-dom';
import { registerWithInvitation } from '../../services/userService';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const { login, authError } = useAuth();
  const { companyInfo } = useTheme();
  const navigate = useNavigate();

  // Check if user came from password reset
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('reset') === 'success') {
      setResetSuccess(true);
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Registration with invitation states
  const [showRegistration, setShowRegistration] = useState(false);
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const success = await login(email, password);
      if (success) {
        navigate('/dashboard');
      } else {
        setError(authError || 'Invalid email or password');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    setRegError('');
    setRegSuccess('');

    if (regPassword !== confirmPassword) {
      setRegError('Passwords do not match');
      return;
    }

    if (regPassword.length < 6) {
      setRegError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);
    try {
      await registerWithInvitation(regEmail, regPassword, invitationCode);
      setRegSuccess('Registration successful! You can now login with your credentials.');
      setShowRegistration(false);
      setRegEmail('');
      setRegPassword('');
      setConfirmPassword('');
      setInvitationCode('');
    } catch (err) {
      setRegError(err.message || 'Registration failed. Please check your invitation code.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#111111]">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl shadow-lg overflow-hidden max-w-md w-full"
      >
        <div className="p-8">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="flex justify-center mb-4">
                <img 
                  src={companyInfo.favicon || companyInfo.logo || "/apex-logo-placeholder.png"} 
                  alt={companyInfo.name || "Apex Interio"} 
                  className="h-16 w-auto"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iIzNiNDJjYyIvPjxwYXRoIGQ9Ik0zMCA3MEw1MCAzMEw3MCA3MFoiIGZpbGw9IiNmZmZmZmYiLz48L3N2Zz4=';
                  }} 
                />
              </div>
              <h2 className="text-3xl font-bold text-gray-800">{companyInfo.name || "Apex Interio"}</h2>
              <p className="text-gray-500">Quotation Management System</p>
            </motion.div>
          </div>

          {(regSuccess || resetSuccess) && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-50 text-green-600 p-3 rounded-lg mb-4 text-sm"
            >
              {regSuccess || (resetSuccess && 'Password reset email sent! Please check your inbox and follow the instructions to reset your password.')}
            </motion.div>
          )}

          {!showRegistration ? (
            <form onSubmit={handleLogin}>
            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm"
              >
                {error}
              </motion.div>
            )}
            
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E30613] focus:border-[#E30613]"
                placeholder="admin@apexinterio.in"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E30613] focus:border-[#E30613]"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-2 px-4 rounded-lg text-white font-medium transition-colors ${
                isLoading ? 'bg-[#F9B3B7]' : 'bg-[#E30613] hover:bg-[#C70511]'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
            </button>

            <div className="text-center mt-4 space-y-3">
              <Link
                to="/reset-password"
                className="block text-[#E30613] hover:text-[#C70511] text-sm font-medium transition-colors"
              >
                Forgot your password?
              </Link>
              
              <div className="border-t border-gray-200 pt-3">
                <button
                  type="button"
                  onClick={() => setShowRegistration(true)}
                  className="text-[#E30613] hover:text-[#C70511] text-sm font-medium"
                >
                  Have an invitation code? Register here
                </button>
              </div>
            </div>
          </form>
        ) : (
          <form onSubmit={handleRegistration}>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Register with Invitation</h3>
            </div>

            {regError && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm"
              >
                {regError}
              </motion.div>
            )}
            
            <div className="mb-4">
              <label htmlFor="regEmail" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="regEmail"
                type="email"
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E30613] focus:border-[#E30613]"
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="invitationCode" className="block text-sm font-medium text-gray-700 mb-1">
                Invitation Code
              </label>
              <input
                id="invitationCode"
                type="text"
                value={invitationCode}
                onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E30613] focus:border-[#E30613]"
                placeholder="Enter your invitation code"
                required
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="regPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="regPassword"
                type="password"
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E30613] focus:border-[#E30613]"
                required
              />
            </div>

            <div className="mb-6">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E30613] focus:border-[#E30613]"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-2 px-4 rounded-lg text-white font-medium transition-colors mb-4 ${
                isLoading ? 'bg-green-400' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {isLoading ? 'Registering...' : 'Register'}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowRegistration(false)}
                className="text-[#E30613] hover:text-[#C70511] text-sm font-medium"
              >
                Back to Login
              </button>
            </div>
          </form>
        )}
        </div>
        
        <div className="bg-gray-50 p-4 text-center text-sm text-gray-600 border-t">
          © {new Date().getFullYear()} {companyInfo.name || "Apex Interio"}. All rights reserved.
        </div>
      </motion.div>
    </div>
  );
};

export default Login;